import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useSelector } from 'react-redux'
import { CopilotMessage } from './lib/copilotTypes'
import { VoiceWaveIcon, UserIcon, MicIcon, SparkIcon } from './icons'
import { cx } from './cx'
import WidgetRenderer from './widgets/WidgetRenderer'
import styles from './index.module.scss'

interface MessageBubbleProps {
  message: CopilotMessage
  fresh?: boolean
}

/**
 * The human on the other side of the copilot IS the business owner, so their
 * avatar mirrors the account avatar the top-nav shows (me.profile_picture, as
 * in header/right-header.js), with a graceful glyph fallback when it's empty
 * or the image fails to load.
 */
const useUserAvatarUrl = (): string | undefined =>
  useSelector((state: { auth: { me?: { profile_picture?: string } } }) => {
    const url = state.auth.me && state.auth.me.profile_picture
    return url || undefined
  })

const UserAvatar: React.FC = () => {
  const avatarUrl = useUserAvatarUrl()
  const [failed, setFailed] = useState(false)
  if (avatarUrl && !failed) {
    return <img className={styles.avatarImg} src={avatarUrl} alt="" onError={() => setFailed(true)} />
  }
  return <UserIcon />
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, fresh }) => {
  const isUser = message.role === 'user'
  return (
    <div className={cx(styles.row, isUser && styles.user, !isUser && fresh && styles.fresh)}>
      <div className={cx(styles.miniAvatar, isUser && styles.miniAvatarUser)}>
        {isUser ? <UserAvatar /> : <VoiceWaveIcon />}
      </div>
      <div className={cx(styles.bubble, isUser ? styles.bubbleUser : styles.bubbleAssistant)}>
        {isUser && message.voice && (
          <span className={styles.voicePrefix}>
            <MicIcon />
          </span>
        )}
        <div className={styles.markdown}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
        </div>
        {/* widget seam: rich display cards (final.data.widgets) render below the prose.
            Assistant turns only; the renderer no-ops on user messages / no widgets. */}
        {!isUser && <WidgetRenderer widgets={message.widgets} />}
        {message.chip && (
          <span className={styles.dataChip}>
            <SparkIcon />
            {message.chip}
          </span>
        )}
      </div>
    </div>
  )
}

export default MessageBubble
